import { Button } from "@/design-system/Button/Button";
import { Collapsible } from "@/design-system/Collapsible/Collapsible";
import {
  type DropdownItem,
  MultiDropdown,
} from "@/design-system/Dropdown/Dropdown";
import { Pagination } from "@/design-system/Pagination/Pagination";
import { cn } from "@/lib/utils";
import {
  applyEvolutionStep,
  chainTagLabel,
  getCurrentEvolutionPlayerSelector,
} from "@/redux/evolutions/evolutionsBuilderSlice";
import { BREAKPOINTS } from "@/settings";
import {
  availableChainTags,
  sortAndFilterChains,
} from "@repo/shared/evolutions";
import { useTranslation } from "@repo/shared/i18n";
import type {
  EvolutionChainRecommendation,
  EvolutionChainTagType,
} from "@repo/shared/types";
import { ChevronDown } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useMediaQuery } from "usehooks-ts";

import type { ChainWallMode } from "../../sections/EvolutionChain";
import { ChainRecommendationSkeletonCard } from "../../sections/EvolutionsStepsSkeleton";
import { EvoStepCard, EvolutionChainTag } from "./EvoStepCard";

export interface EvolutionsBuilderChainRecommendationsProps {
  evolutionChainRecommendations: EvolutionChainRecommendation[];
  loading: boolean;
  onAddChainToEvolutionChain: (chain: EvolutionChainRecommendation) => void;
  onClickUnlockChains?: () => void;
  triesLeft?: number;
  chainWall?: ChainWallMode;
  searchTerm?: string;
}

const ITEMS_PER_PAGE = 5;

/**
 * "Chain Recommendations": the best chain for the player up front, the rest
 * folded behind "Show more" and paged. Chains can be narrowed by tag.
 */
export const EvolutionsBuilderChainRecommendations = ({
  evolutionChainRecommendations,
  loading,
  onAddChainToEvolutionChain,
  onClickUnlockChains,
  chainWall,
  searchTerm,
}: EvolutionsBuilderChainRecommendationsProps) => {
  const { t } = useTranslation("evoBuilder");
  const { t: tCommon } = useTranslation("common");
  const [isOpen, setIsOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const isTouchDevice = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);

  const availableTags = useMemo(
    () => availableChainTags(evolutionChainRecommendations),
    [evolutionChainRecommendations],
  );

  /*
    Any number of tags, picked from a multi-select; a chain shows when it
    carries at least one of them. Nothing picked is the unfiltered list. A
    tag that has left the list (new player, new chains) drops out silently.
  */
  const [pickedTags, setPickedTags] = useState<EvolutionChainTagType[]>([]);
  const selectedTags = useMemo<EvolutionChainTagType[]>(
    () => pickedTags.filter((tag) => availableTags.includes(tag)),
    [pickedTags, availableTags],
  );
  const handleClearTagFilter = () => setPickedTags([]);
  const handleToggleTag = (tag: EvolutionChainTagType) =>
    setPickedTags((tags) =>
      tags.includes(tag) ? tags.filter((x) => x !== tag) : [...tags, tag],
    );
  const tagItems: DropdownItem[] = availableTags.map((tag) => ({
    value: tag,
    label: chainTagLabel(tag, t),
    render: <EvolutionChainTag tag={tag} />,
  }));

  const sortedChains = useMemo(
    () =>
      sortAndFilterChains(evolutionChainRecommendations, {
        searchTerm,
        selectedTags,
      }),
    [evolutionChainRecommendations, selectedTags, searchTerm],
  );

  const chain = sortedChains[0] || null;
  const remainingChains = sortedChains.slice(1);

  const totalPages = Math.ceil(remainingChains.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedChains = remainingChains.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [remainingChains.length]);

  const currentEvolutionPlayer = useSelector(getCurrentEvolutionPlayerSelector);

  const handleChainRecommendationClick = (
    chain: EvolutionChainRecommendation,
  ) => {
    onAddChainToEvolutionChain(chain);
    if (availableTags.length > 0) handleClearTagFilter();
    setIsOpen(false);
  };

  /*
    A phone has room for one tag: the first one picked in the filter, else
    the chain's first. Larger screens show the picked tags, or all of them.
  */
  const cardTags = (chain: EvolutionChainRecommendation) => {
    if (isMobile) {
      const tag = selectedTags[0] ?? chain.tags?.[0];
      return tag ? [tag] : [];
    }
    return selectedTags.length > 0 && isTouchDevice ? selectedTags : chain.tags;
  };

  const card = (chain: EvolutionChainRecommendation, key?: string) => (
    <EvoStepCard
      key={key}
      {...chain}
      size="chain"
      tags={cardTags(chain)}
      onClick={() => handleChainRecommendationClick(chain)}
      loading={loading}
      player={applyEvolutionStep(currentEvolutionPlayer, chain)}
      metaRatingBeforeApplyingUpgrade={currentEvolutionPlayer?.metaRating}
      playerRoleIdBeforeApplyingUpgrade={currentEvolutionPlayer?.playerRoleId}
      onClickUnlockChains={onClickUnlockChains}
      chainWall={chainWall}
    />
  );

  return (
    <div className="flex w-full flex-col gap-2 mt-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="pl-4 text-sm font-semibold text-foreground">
          {t("recommendations.title")}
        </h3>
        {availableTags.length > 0 && (
          <MultiDropdown
            variant="secondary"
            hideSearch
            align="end"
            width={180}
            triggerWidth={isMobile ? 96 : 128}
            items={tagItems}
            label={isMobile ? t("chainTags.labelShort") : t("chainTags.label")}
            selected={selectedTags}
            onChange={(item) =>
              handleToggleTag(item.value as EvolutionChainTagType)
            }
          />
        )}
      </div>

      {loading ? (
        <ChainRecommendationSkeletonCard />
      ) : (
        chain && (
          <EvoStepCard
            {...chain}
            size="chain"
            tags={cardTags(chain)}
            onClick={() => handleChainRecommendationClick(chain)}
            loading={loading}
            player={applyEvolutionStep(currentEvolutionPlayer, chain)}
            metaRatingBeforeApplyingUpgrade={currentEvolutionPlayer?.metaRating}
            playerRoleIdBeforeApplyingUpgrade={
              currentEvolutionPlayer?.playerRoleId
            }
            onClickUnlockChains={onClickUnlockChains}
            chainWall={chainWall}
          />
        )
      )}

      {remainingChains.length > 0 && (
        <>
          <Collapsible open={isOpen}>
            <div className="flex flex-col gap-2 pb-4">
              {paginatedChains.map((chain, index) =>
                loading ? (
                  <ChainRecommendationSkeletonCard key={index} />
                ) : (
                  card(
                    chain,
                    `${chain.evolutionIds?.join("-")}-${startIndex + index}`,
                  )
                ),
              )}
              {remainingChains.length > ITEMS_PER_PAGE && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onChange={setCurrentPage}
                  caption={t("recommendations.showing", {
                    from: startIndex + 1,
                    to: Math.min(endIndex, remainingChains.length),
                    total: remainingChains.length,
                  })}
                />
              )}
            </div>
          </Collapsible>
          <div className="mx-auto">
            <Button
              variant="secondary"
              size="small"
              icon={
                <ChevronDown
                  className={cn(
                    "size-4 transition-transform duration-300 ease-in-out",
                    isOpen && "rotate-180",
                  )}
                />
              }
              iconPosition="right"
              onClick={() =>
                setIsOpen((open) => {
                  if (open) window.scrollTo({ top: 0 });
                  return !open;
                })
              }
            >
              {isOpen ? tCommon("showLess") : tCommon("showMore")}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
